import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { formatIndianPhone } from "../../components/PhoneLink";
import { listNotReadyAlerts, markNotReadyAlertSeen } from "../../../services/tripTrackingApi";
import { clock, useAlarmSound } from "./SilenceAlerts";

// The server checks every minute; polling as often keeps the page at most one check behind.
const POLL_MS = 60_000;
const SOUNDED_KEY = "tripNotReadyAlertsSounded";

/**
 * Trips past their pickup time with Trip Preparation not done, polled while Manage Bookings is open.
 * Sounds like the no-location alerts - at the alert and at its reminder, unless an admin clicked
 * Seen - and says so when one clears.
 */
export function useNotReadyAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [seenBusy, setSeenBusy] = useState(null);
  // The alerts from the last poll that answered; null until the first, so a page load says nothing.
  const previous = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const next = await listNotReadyAlerts();
      const ids = new Set(next.map((entry) => entry.id));
      (previous.current || []).forEach((entry) => {
        if (ids.has(entry.id)) return;
        toast.success(`Booking #${entry.bookingId}: alert cleared - Trip Preparation is done, or the trip was completed or cancelled.`);
      });
      previous.current = next;
      setAlerts(next);
    } catch {
      // The next poll retries; what is on screen stays.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const { soundBlocked, enableSound } = useAlarmSound(alerts, SOUNDED_KEY);

  const markSeen = useCallback(async (entry) => {
    setSeenBusy(entry.id);
    try {
      const updated = await markNotReadyAlertSeen(entry.id);
      setAlerts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (error) {
      window.alert(error?.response?.data?.message || "Could not mark the alert as seen.");
    } finally {
      setSeenBusy(null);
    }
  }, []);

  return { alerts, soundBlocked, seenBusy, markSeen, enableSound };
}

/** Above the bookings table, under the no-location alerts: each trip with what is still to do. */
export function NotReadyAlertPanel({ alerts, soundBlocked, seenBusy, onSeen, onEnableSound, onShowBooking }) {
  if (!alerts.length) return null;
  return (
    <div className="booking-silence-alerts" role="alert">
      <div className="booking-silence-alerts-head">
        <span><i className="bi bi-clipboard-x me-1" aria-hidden="true" />Trips past pickup with Trip Preparation not ready ({alerts.length})</span>
        {soundBlocked && (
          <Button size="sm" variant="light" onClick={onEnableSound}>
            <i className="bi bi-volume-up me-1" aria-hidden="true" />Turn on sound
          </Button>
        )}
      </div>
      {alerts.map((entry) => {
        const { display, dial } = formatIndianPhone(entry.driverPhone);
        return (
          <div key={entry.id} className={`booking-silence-alert${entry.seenAtIst ? " seen" : ""}`}>
            <div className="booking-silence-alert-text">
              <div className="fw-semibold">
                #{entry.bookingId} · {entry.tripTitle || "Trip"}{entry.customerName ? ` · ${entry.customerName}` : ""}
              </div>
              <div>
                Pickup was at {clock(entry.pickupAtIst)}
                {entry.tripStatus === "ONGOING" ? " and the driver has started the trip." : " - the booking stays Upcoming until this is done:"}
              </div>
              <ul className="booking-not-ready-list">
                {(entry.missing || []).map((item) => <li key={item}>{item}</li>)}
              </ul>
              <div className="small">
                Driver: {entry.driverName || "-"}{display ? ` · ${display}` : ""}
                {" · "}Office emailed {clock(entry.alertedAtIst)}
                {entry.remindedAtIst ? `, reminder ${clock(entry.remindedAtIst)}` : ""}
              </div>
            </div>
            <div className="booking-silence-alert-actions">
              <Button size="sm" variant="danger" onClick={() => onShowBooking(entry.bookingId)}>
                Show booking
              </Button>
              {dial && (
                <Button size="sm" variant="outline-danger" href={`tel:${dial}`}>
                  <i className="bi bi-telephone-fill me-1" aria-hidden="true" />Call driver
                </Button>
              )}
              {entry.seenAtIst ? (
                <span className="booking-silence-seen">Seen by {entry.seenBy || "admin"} at {clock(entry.seenAtIst)}</span>
              ) : (
                <Button
                  size="sm"
                  variant="dark"
                  disabled={seenBusy === entry.id}
                  title="Stops the sound for every admin and shows them you are handling it"
                  onClick={() => onSeen(entry)}
                >
                  {seenBusy === entry.id ? <Spinner size="sm" /> : "Seen"}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
